import { useEffect, useState, type FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { accountsService } from '@/services/accounts.service';
import { ApiError } from '@/services/http';
import { useAccountStore } from '@/store/account.store';

export interface RequestAffiliateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * RequestAffiliateModal — the "Request to Become an Affiliate" application
 * form. Mirrors the codebase form convention (FormField + plain useState,
 * no react-hook-form/zod). First/last name prefill from the cached account
 * so the common case is one-click. On success we patch
 * `affiliateStatus = 'pending'` straight onto the store from the refreshed
 * AccountView the server returns, so the locked overlay flips to its
 * "under review" state without a follow-up `/accounts/me`.
 */
export function RequestAffiliateModal({ open, onOpenChange }: RequestAffiliateModalProps) {
  const account = useAccountStore((s) => s.account);
  const patchAccount = useAccountStore((s) => s.patchAccount);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [contactUrl, setContactUrl] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Prefill name from the account each time the modal opens (and clear the
  // contact fields so a re-open never shows stale input).
  useEffect(() => {
    if (!open) return;
    setFirstName(account?.firstName ?? '');
    setLastName(account?.lastName ?? '');
    setContactUrl('');
    setContactEmail('');
  }, [open, account?.firstName, account?.lastName]);

  const trimmed = {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    contactUrl: contactUrl.trim(),
    contactEmail: contactEmail.trim(),
  };
  const canSubmit =
    trimmed.firstName.length > 0 &&
    trimmed.lastName.length > 0 &&
    trimmed.contactUrl.length > 0 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed.contactEmail);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const updated = await accountsService.requestAffiliate(trimmed);
      patchAccount(updated);
      toast.success('Request submitted — we’ll review it shortly.');
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to submit request.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-or-slate-900/55 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-32px)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-or-slate-100 bg-white p-6 shadow-or-pop focus:outline-none"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-or-orange-200 bg-or-cream-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-or-orange-700">
                <Sparkles className="h-3 w-3" aria-hidden />
                Affiliate program
              </div>
              <Dialog.Title className="mt-2 font-display text-[20px] font-bold text-or-slate-900">
                Request to become an affiliate
              </Dialog.Title>
              <p className="mt-1 text-[13px] leading-relaxed text-or-slate-600">
                Tell us a little about how you’ll share OllieReads. We review every
                request and you’ll get access to your link and payouts once approved.
              </p>
            </div>
            <Dialog.Close
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-or-slate-400 transition hover:bg-or-slate-100 hover:text-or-slate-700 disabled:opacity-50"
              aria-label="Close"
              disabled={submitting}
            >
              <X className="h-4 w-4" aria-hidden />
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                label="First name"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                maxLength={120}
                autoComplete="given-name"
              />
              <FormField
                label="Last name"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                maxLength={120}
                autoComplete="family-name"
              />
            </div>
            <FormField
              label="Website or social media account"
              required
              value={contactUrl}
              onChange={(e) => setContactUrl(e.target.value)}
              maxLength={500}
              placeholder="https://instagram.com/yourhandle"
              help="A link to where you’ll be sharing OllieReads."
            />
            <FormField
              label="Email"
              required
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              maxLength={320}
              placeholder="you@example.com"
              help="Visit your account settings to check your approval status"
              autoComplete="email"
            />

            <div className="mt-2 flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" size="md" disabled={!canSubmit || submitting}>
                {submitting ? 'Submitting…' : 'Submit request'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
